import AsyncStorage from '@react-native-async-storage/async-storage';
import { versionAuMoins } from '../config/offres';
import { VERSION_POLITIQUE_COLLECTE } from '../config/collecte';
import { VERSION_DOCUMENTS } from '../config/terms';

/**
 * FENÊTRE « NOUVEAUTÉS » — montrée UNE fois, et seulement à qui MET À JOUR.
 *
 * Version dont le contenu est à montrer (textes : `nouveautes.*` des dictionnaires). Pour une
 * prochaine version : changer cette constante et les textes, rien d'autre.
 */
export const VERSION_NOUVEAUTES = '1.1.1';

/** Dernière version dont les nouveautés ont été vues, ou marquée vue à la première installation. */
export const CLE_VERSION_VUE = 'nouveautes_version_vue';

/**
 * MARQUEURS D'UNE INSTALLATION ANTÉRIEURE — tous écrits par la 1.0.5 et la 1.1.0, aucun par une
 * installation qui n'a jamais tourné.
 *
 * - `app_version_policy` : cache de la politique de version, écrit à CHAQUE lancement connecté ou
 *   non depuis le 01/09/2026 (avant la 1.0.5), dès que la lecture réseau aboutit. Le marqueur
 *   principal : il ne dépend pas de la connexion.
 * - la session Supabase (`storageKey` de `services/supabase.ts`) et `lastSessionPing` : présents chez
 *   qui était connecté, même sans réseau au dernier lancement.
 *
 * Lus AVANT toute écriture de la nouvelle version : `App` lance cette lecture dans son premier effet,
 * déclaré avant `useVersionGate`, qui n'écrit son cache qu'après un aller-retour réseau.
 */
const MARQUEURS_INSTALLATION_ANTERIEURE = [
  'app_version_policy',
  'sb-swwksuskwuhqzozofbno-auth-token',
  'lastSessionPing',
];

/**
 * Faut-il montrer les nouveautés ? Une première installation n'en voit JAMAIS : elle est marquée
 * comme ayant vu la version installée, et ne les verra qu'à sa prochaine mise à jour.
 * Toute lecture en échec vaut « non » : une fenêtre manquée vaut mieux qu'une fenêtre en boucle.
 */
export async function nouveautesAMontrer(versionInstallee: string | null): Promise<boolean> {
  if (!versionInstallee || !versionAuMoins(versionInstallee, VERSION_NOUVEAUTES)) return false;
  try {
    const [vue, ...marqueurs] = await Promise.all(
      [CLE_VERSION_VUE, ...MARQUEURS_INSTALLATION_ANTERIEURE].map((cle) => AsyncStorage.getItem(cle)),
    );
    if (vue !== null) return !versionAuMoins(vue, VERSION_NOUVEAUTES);
    const miseAJour = marqueurs.some((valeur) => valeur !== null);
    if (!miseAJour) await AsyncStorage.setItem(CLE_VERSION_VUE, versionInstallee);
    return miseAJour;
  } catch {
    return false;
  }
}

/** Fenêtre fermée : ces nouveautés ne reviendront plus. */
export async function marquerNouveautesVues(versionInstallee: string | null): Promise<void> {
  try {
    await AsyncStorage.setItem(CLE_VERSION_VUE, versionInstallee ?? VERSION_NOUVEAUTES);
  } catch {
    // Non retenu : elle reviendra au prochain lancement. Gênant, jamais bloquant.
  }
}

/** Dernière politique de confidentialité annoncée dans la fenêtre, ou marquée annoncée à l'installation. */
export const CLE_POLITIQUE_ANNONCEE = 'nouveautes_politique_vue';

/**
 * POLITIQUE 1.12 : le § 12 promet une notification de toute modification substantielle.
 *
 * Déclencheur distinct de celui de la version : la fenêtre de version ne s'ouvre qu'une fois par
 * binaire, et qui l'a fermée en 1.1.1 ne la reverrait jamais si la politique arrive par mise à jour
 * à distance. Condition de version : celle de la garde de collecte (`collecteAppActive`, verrou a),
 * pour que l'annonce ne précède jamais le texte qui décrit la mesure.
 *
 * La clé de version vue compte comme trace d'installation antérieure : `preparer` lit donc la
 * politique AVANT `nouveautesAMontrer`, qui l'écrit à la première installation.
 */
export async function politiqueAAnnoncer(versionDocuments: string = VERSION_DOCUMENTS): Promise<boolean> {
  if (!versionAuMoins(versionDocuments, VERSION_POLITIQUE_COLLECTE)) return false;
  try {
    const [annoncee, ...traces] = await Promise.all(
      [CLE_POLITIQUE_ANNONCEE, CLE_VERSION_VUE, ...MARQUEURS_INSTALLATION_ANTERIEURE].map((cle) => AsyncStorage.getItem(cle)),
    );
    if (annoncee !== null) return !versionAuMoins(annoncee, VERSION_POLITIQUE_COLLECTE);
    // Première installation : la politique en vigueur est celle acceptée à l'inscription.
    const installationAnterieure = traces.some((valeur) => valeur !== null);
    if (!installationAnterieure) await AsyncStorage.setItem(CLE_POLITIQUE_ANNONCEE, VERSION_POLITIQUE_COLLECTE);
    return installationAnterieure;
  } catch {
    return false;
  }
}

/** Fenêtre fermée : la politique ne sera plus annoncée. */
export async function marquerPolitiqueAnnoncee(): Promise<void> {
  try {
    await AsyncStorage.setItem(CLE_POLITIQUE_ANNONCEE, VERSION_POLITIQUE_COLLECTE);
  } catch {
    // Non retenu : l'annonce reviendra au prochain lancement. Gênant, jamais bloquant.
  }
}
